import { cva, type VariantProps } from 'class-variance-authority'
import { Slot as SlotPrimitive } from 'radix-ui'
import { cn } from '~/lib/utils'

// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = cva(
	"inline-flex w-max shrink-0 items-center justify-center gap-[8px] whitespace-nowrap font-700 leading-none! outline-none transition-all focus-visible:border-ring focus-visible:ring-[2px] focus-visible:ring-ring disabled:cursor-not-allowed! aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg:not([class*='size-'])]:size-[32px] [&_svg]:pointer-events-none [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				primary: 'bg-primary text-primary-foreground enabled:active:bg-light-blue-75 enabled:hover:bg-light-blue-25 disabled:bg-dark-blue-50 disabled:text-dark-blue-100',
				secondary: 'bg-secondary text-secondary-foreground enabled:active:bg-teal-75 enabled:hover:bg-teal-50 disabled:bg-light-grey-100 disabled:text-dark-blue-100',
				destructive: 'bg-destructive text-destructive-foreground enabled:active:bg-light-red-50 enabled:hover:bg-light-red-25 disabled:bg-light-red-25 disabled:text-navy-50',
				outline:
					'border border-navy-100 bg-background text-navy-100 enabled:active:bg-light-grey-25 enabled:hover:bg-light-blue-25 disabled:border-dark-grey-75 disabled:bg-light-grey-25 disabled:bg-white disabled:text-disabled-foreground disabled:text-muted-foreground!',
				'outline-inverse':
					'border border-white bg-transparent text-white enabled:active:bg-navy-50 enabled:hover:bg-navy-75 disabled:border-dark-grey-75 disabled:bg-light-grey-25 disabled:bg-white disabled:text-disabled-foreground disabled:text-muted-foreground!',
				ghost: 'border border-transparent bg-background text-navy-100 enabled:active:border-navy-100 enabled:active:bg-light-grey-25 enabled:hover:bg-light-blue-25 disabled:text-muted-foreground',
				link: 'text-link-100 underline-offset-4 enabled:hover:underline',
			},
			size: {
				xl: 'h-[48px] px-[16px] text-[18px]',
				lg: 'h-[40px] px-[16px] text-[18px]',
				md: 'h-[32px] px-[12px] py-[6px] text-[14px]',
				sm: 'h-[24px] gap-[4px] px-[10px] text-[14px]',
				icon: 'size-[32px]',
				'icon-sm': 'size-[24px]',
				'icon-lg': 'size-[42px]',
			},
		},
		defaultVariants: {
			variant: 'primary',
			size: 'md',
		},
	},
)

export function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean
	}) {
	const Comp = asChild ? SlotPrimitive.Slot : 'button'

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			type={props.type ?? 'button'}
			{...props}
		/>
	)
}
